import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { WindowEnums } from 'src/app/enum/window-enums';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { PermissionsProfileUserComponent } from '../permissions-profile-user/permissions-profile-user.component';
import { ProfileUserModel } from './../../../../models/authentication/profile-user.model';
import { ProfileUserService } from './../../../../services/authentication/profile-user.service';

@Component({
  selector: 'app-register-profile-user',
  templateUrl: './register-profile-user.component.html',
  styleUrls: ['./register-profile-user.component.scss'],
  standalone: false
})
export class RegisterProfileUserComponent implements OnInit, OnDestroy {
  @ViewChild('permissionProfile') permissionProfileModalComponent?: CustomModalComponent | null = null;

  componentPermissionProfile = PermissionsProfileUserComponent;
  disabledForm: boolean = false;
  profileUserContract: ProfileUserModel = new ProfileUserModel();
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Perfiles';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  permissionsUser: OptionWindowModel[] = [];
  url: string = 'profile-user';
  textModal: string = 'Permisos Perfil';
  formFamily: FormGroup = new FormGroup({});
  profileUserId: number = 0;
  userAuthenticated: UserModel = new UserModel();
  formProfileUser: FormGroup = new FormGroup({});
  inputsForPermissionProfile$ = new BehaviorSubject<any>({
    profileUserModel: new ProfileUserModel,
    permissionUpdatable: false
  });

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private profileUserService: ProfileUserService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private headerService: HeaderService,
    private location: Location,
    private optionWindowService: OptionWindowService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.validateForm(new ProfileUserModel());
    await this.getPermission();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    this.activatedRoute.params.subscribe(params => {
      this.profileUserId = Object.keys(params).length > 0 ? params?.id : null;
    });

    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeProfileUser() {
    let code = this.formProfileUser.get("code")?.value;
    code = code === "" ? null : code;
    if (this.profileUserContract.code == code) return;
    if (code == null) {
      this.resetProfileUser();
      return;
    }

    try {
      this.profileUserContract = await firstValueFrom(this.profileUserService.getProfileUserByCode(code));
      this.profileUserId = this.profileUserContract.profileUserId;
    } catch (error) {
      console.error('Error al buscar el perfil.', error);
    }
    await this.validateForm(this.profileUserContract);
  }

  async createNewProfileUser() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para crear perfiles. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    await this.resetProfileUser();

    this.disabledForm ? this.formProfileUser.disable() : this.formProfileUser.enable();
    const control = this.formProfileUser.controls["code"];
    this.profileUserId !== 0 && this.profileUserId !== null ? control.enable() : control.disable();
  }

  async deleteProfileUser() {
    if (!this.validations()) return;
    if (!await this.confirmDialogService.confirm(
      `¿Realmente desea eliminar el pefil ${this.profileUserContract.name}?`
    )) return;
    try {
      await firstValueFrom(this.profileUserService.deleteProfileUser(this.profileUserContract.profileUserId));
      this.toastHandlerService.showToast(
        'success',
        'El perfil fue eliminado exitosamente.'
      );
      this.createNewProfileUser();
    } catch (error) {
      console.error('Error al eliminar el perfil.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'warning',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }

    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      await this.getPermissionsForUserByWindow();
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.ProfileUser));
  }

  async getProfileUserById() {
    try {
      this.profileUserContract = await firstValueFrom(this.profileUserService.getProfileUserById(this.profileUserId));
      this.profileUserId = this.profileUserContract.profileUserId;
      await this.validateForm(this.profileUserContract);
    } catch (error) {
      console.error('Error al buscar el usuario.', error);
    }
  }

  async handleInitialState() {
    if (this.profileUserId !== null && this.profileUserId !== 0) {
      await this.getProfileUserById();
    } else {
      this.createNewProfileUser();
    }
  }

  openPermissionProfile() {
    this.inputsForPermissionProfile$.next({
      profileUserModel: this.profileUserContract,
      permissionUpdatable: this.permissionUpdatable
    });

    this.permissionProfileModalComponent && this.permissionProfileModalComponent.show();
  }

  async resetProfileUser() {
    this.profileUserId = 0;
    this.profileUserContract = new ProfileUserModel();
    await this.validateForm(this.profileUserContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async send(): Promise<any> {
    Object.keys(this.formProfileUser.controls).forEach(key => {
      const control = this.formProfileUser.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formProfileUser.valid) {
      const modelUser = this.formProfileUser.getRawValue();

      if (this.profileUserId == 0) {
        try {
          const response = await firstValueFrom(this.profileUserService.createProfileUser(modelUser));
          this.toastHandlerService.showToast(
            'success',
            `El perfil fue creada exitosamente.`
          );
          this.profileUserContract = response;
          this.profileUserId = this.profileUserContract.profileUserId;
          this.validateForm(this.profileUserContract);
          this.location.replaceState(`/${this.url}/register/${this.profileUserId}`);
        } catch (error) {
          console.error('Error al crear el perfil.', error);
        }
      } else {
        this.profileUserContract.name = modelUser.name;
        try {
          const response = await firstValueFrom(this.profileUserService.updateProfileUser(this.profileUserContract));
          this.toastHandlerService.showToast(
            'success',
            `El perfil fue modificada exitosamente.`
          );
          this.profileUserContract = response;
          this.validateForm(this.profileUserContract);
        } catch (error) {
          console.error('Error al actualizar el perfil.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      name: {
        required: 'El campo nombres es obligatorio.',
        maxlength: 'El campo nombres permite máximo 150 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formProfileUser.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast(
              'warning',
              message,
            );
          }
        });
      }
    });
  }

  validations() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para modificar el perfil. Comuníquese con el administrador del sistema'
      );
      return false;
    }

    if (this.profileUserContract.profileUserId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un perfil.'
      );
      return false;
    }
    return true;
  }

  validateForm(profileUser: ProfileUserModel) {
    this.formProfileUser = this.formBuilder.group({
      code: [profileUser.code, [Validators.required,]],
      name: [profileUser.name, [Validators.required, Validators.maxLength(150)]]
    });

    if (!this.permissionUpdatable) setTimeout(() => this.formProfileUser.disable());
    this.formControlStateService.setControlState(this.formProfileUser, ['code'], true);
    if (profileUser.profileUserId != 0) {
      this.textModal = `Permisos Perfil - ${profileUser.name}`;
    }
  }

}
